import { WebApp } from "../constants"
import type { BottomButton as TelegramBottomButton } from "../types"

type AtLeastOne<T> = {
  [K in keyof T]: Pick<T, K>
}[keyof T] &
  Partial<T>

export type ButtonOptions = Pick<
  TelegramBottomButton,
  "text" | "color" | "textColor" | "hasShineEffect" | "isVisible" | "isActive" | "position"
>

const DEFAULT_MAIN_PARAMS: ButtonOptions = {
  text: "Save",
  color: "#EAB308",
  textColor: "#111827",
  hasShineEffect: true,
  position: "right",
  isActive: true,
  isVisible: false
}

const DEFAULT_SECONDARY_PARAMS: ButtonOptions = {
  text: "Reset",
  color: "#374151",
  textColor: "#D1D5DB",
  hasShineEffect: true,
  position: "left",
  isActive: true,
  isVisible: false
}

interface Subscription {
  skip: boolean
}

export class BottomButtonController {
  private subscriptions = new Map<() => void, Subscription>()

  constructor(
    private button: TelegramBottomButton,
    defaultParams: ButtonOptions
  ) {
    this.button.onClick(this.handleClick)
    this.setParams(defaultParams)
  }

  private handleClick = (): void => {
    for (const [handler, subscription] of this.subscriptions) {
      if (!subscription.skip) {
        handler()
      }
    }
  }

  subscribe(handler: () => void, skip = true): () => void {
    this.subscriptions.set(handler, { skip })

    return () => {
      this.subscriptions.delete(handler)
    }
  }

  setSkip(handler: () => void, skip: boolean): void {
    const subscription = this.subscriptions.get(handler)

    if (subscription) {
      subscription.skip = skip
    }
  }

  get subscriberCount(): number {
    return this.subscriptions.size
  }

  get hasActiveHandlers(): boolean {
    for (const subscription of this.subscriptions.values()) {
      if (!subscription.skip) return true
    }
    return false
  }

  setParams(params: AtLeastOne<ButtonOptions>): void {
    const currentParams = this.params
    let hasChanges = false

    for (const key of Object.keys(params) as Array<keyof ButtonOptions>) {
      if (params[key] !== currentParams[key]) {
        hasChanges = true
        break
      }
    }

    if (!hasChanges) return

    this.button.setParams({
      text: params.text ?? currentParams.text,
      color: params.color ?? currentParams.color,
      text_color: params.textColor ?? currentParams.textColor,
      has_shine_effect: params.hasShineEffect ?? currentParams.hasShineEffect,
      position: params.position ?? currentParams.position,
      is_active: params.isActive ?? currentParams.isActive,
      is_visible: params.isVisible ?? currentParams.isVisible
    })
  }

  setText(text: string): this {
    if (text === this.params.text) return this

    this.button.setText(text)
    return this
  }

  show(): void {
    if (this.button.isVisible) return

    this.button.show()
  }

  hide(): void {
    if (!this.button.isVisible) return

    this.button.hide()
  }

  enable(): void {
    this.button.enable()
  }

  disable(): void {
    this.button.disable()
  }

  showEnabled(): void {
    this.show()
    this.enable()
  }

  hideDisabled(): void {
    this.hide()
    this.disable()
  }

  showProgress(leaveActive = false): void {
    this.button.showProgress(leaveActive)
  }

  hideProgress(): void {
    this.button.hideProgress()
  }

  get params(): ButtonOptions {
    return {
      text: this.button.text,
      color: this.button.color,
      textColor: this.button.textColor,
      hasShineEffect: this.button.hasShineEffect,
      position: this.button.position,
      isActive: this.button.isActive,
      isVisible: this.button.isVisible
    }
  }
}

export const MainButtonController = new BottomButtonController(WebApp.MainButton, DEFAULT_MAIN_PARAMS)

export const SecondaryButtonController = new BottomButtonController(WebApp.SecondaryButton, DEFAULT_SECONDARY_PARAMS)
